import React from 'react';
import '../../reports.scss';
import { formatDateSymbol } from '../../../../../utils/constant';
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from 'react-icons/io';
import SaleByProductTableBodyComponent from '../../products/components/saleByProductTableBodyComponent';
import ProfitByProductTableBodyComponent from '../../products/components/profitByProductTableBodyComponent';
import InventorySummaryTableBodyComponent from '../../products/components/inventorySummaryTableBodyComponent';

// Map API label → metrics key
export const formatLabel = (label) => {
	return label
		.replace(/_/g, ' ') // replace _ with space
		.replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize
};

export default function ReportTable({
	activeTab,
	metaData,
	data,
	selectedColumnsList,
	type,
	setParams,
	params,
	sortDirection,
	setSortDirection,
}) {
	const sortIconMap = {
		asc: <IoIosArrowRoundUp />,
		desc: <IoIosArrowRoundDown />,
	};
	const sortDataList = (param) => {
		const newDirection = sortDirection[param.order_by] === 'desc' ? 'asc' : 'desc';
		setSortDirection({ [param.order_by]: newDirection });
		setParams((prev) => ({
			...prev,
			order_by: newDirection,
			sort_by: param.order_by,
		}));
	};
	const periods = metaData.periods;

	function getReportTypeKey(status) {
		switch (status.trim()) {
			case 'Sale By Customers':
				return 'customer_name';
			case 'Sale By Sales Person':
				return 'sales_person_name';
			case 'Sale By Products':
			case 'Profit By Products':
			case 'Inventory Summary':
				return 'product_name';
			case 'Sale By Sales Summary':
				return 'date';
			default:
				return '';
		}
	}

	function getReportTypeLabel(status) {
		switch (status.trim('')) {
			case 'Sale By Customers':
				return 'NAME';
			case 'Sale By Sales Person':
				return 'Sales Person Name';
			case 'Sale By Products':
			case 'Profit By Products':
			case 'Inventory Summary':
				return 'PRODUCT NAME';
			case 'Sale By Sales Summary':
				return 'Date';
			default:
				return '';
		}
	}

	return (
		<div
			className="report-table-container"
			style={{ height: 'calc(100vh - 380px)' }}
		>
			<table className="report-table">
				<thead>
					{/* Top Header */}
					<tr>
						<th
							onClick={() => {
								if (type !== 'compareTable') {
									sortDataList({ order_by: getReportTypeKey(activeTab) });
								}
							}}
							rowSpan={2}
							className={`${type === 'compareTable' ? 'name-col' : 'text-left cursor-pointer'} `}
							style={{ textAlign: 'left' }}
						>
							<div className="d-flex justify-content-start align-items-center gap-4">
								{(getReportTypeLabel(activeTab) === 'PRODUCT NAME' && params.group_by !== 'None' && activeTab !== 'Inventory Summary')
									? 'ITEMS NAME'
									: getReportTypeLabel(activeTab)}
								{type !== 'compareTable'
									? sortIconMap[sortDirection[getReportTypeKey(activeTab)]]
									: ''}
							</div>
						</th>
						{type === 'compareTable' &&
							periods.map((p, i) => (
								<th
									key={i}
									colSpan={selectedColumnsList.length}
								>
									{`${formatDateSymbol(p.from_date)} - ${formatDateSymbol(p.to_date)}`}
								</th>
							))}
					</tr>

					{/* Sub Header */}
					<tr>
						{periods.map((p, i) =>
							selectedColumnsList.map((m, idx) => (
								<th
									className={`${type !== 'compareTable' ? 'cursor-pointer' : ''}`}
									key={`${i}-${idx}`}
									onClick={() => {
										if (type !== 'compareTable') {
											sortDataList({ order_by: m.key });
										}
									}}
								>
									<div className="d-flex justify-content-center align-items-center gap-4">
										{m.label}
										{type !== 'compareTable' ? sortIconMap[sortDirection[m.key]] : ''}
									</div>
								</th>
							)),
						)}
					</tr>
				</thead>

				<tbody>
					{activeTab.toLowerCase() === 'profit by products' ? (
						<ProfitByProductTableBodyComponent
							data={data}
							params={params}
							periods={periods}
							selectedColumnsList={selectedColumnsList}
						/>
					) : activeTab.toLowerCase() === 'sale by products' ? (
						<SaleByProductTableBodyComponent
							data={data}
							params={params}
							periods={periods}
							selectedColumnsList={selectedColumnsList}
						/>
					) : activeTab.toLowerCase() === 'inventory summary' ? (
						<InventorySummaryTableBodyComponent
							data={data}
							params={params}
							periods={periods}
							selectedColumnsList={selectedColumnsList}
						/>
					) : (
						data.map((item) => (
							<tr key={item}>
								<td className="name-col">
									{`${item[getReportTypeKey(activeTab)]} `}
									{activeTab === 'Profit By Products' || activeTab === 'Inventory Summary'
										? ` ( ${item.product_code} ) `
										: ''}
								</td>

								{periods.map((p, i) => {
									const key = p.label;
									const metrics = item.metrics[key] || {};
									return selectedColumnsList.map((m, idx) => (
										<td key={`${i}-${idx}`}>{metrics[m.key] ?? (m.type === 'string' ? 'N/A' : 0)}</td>
									));
								})}
							</tr>
						))
					)}
				</tbody>
				{activeTab.toLowerCase() === 'profit by products' && (
					<tfoot>
						<tr>
							<td className="fw-bold text-start">TOTAL</td>
							{periods.map((p, i) => {
								const periodKey = p.label;
								const totalMetrics = data.reduce((acc, item) => {
									const normalizedMetrics = Object.keys(item.metrics || {}).reduce((obj, key) => {
										obj[key] = item.metrics[key];
										return obj;
									}, {});
									const metrics = normalizedMetrics[periodKey] || {};
									selectedColumnsList.forEach((col) => {
										acc[col.key] = (acc[col.key] || 0) + Number(metrics[col.key] || 0);
									});
									return acc;
								}, {});

								return selectedColumnsList.map((m, idx) => (
									<td
										key={`${i}-${idx}`}
										className="fw-bold"
									>
										{Number(totalMetrics[m.key] || 0).toFixed(2)}
									</td>
								));
							})}
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	);
}
